import { ApiResponse } from '@racefront/types'
import { AxiosError } from 'axios'
import { useCallback, useMemo, useState } from 'react'
import {
  REFRESH_TOKEN,
  SIGN_IN_INITIATE,
  SIGN_IN_VERIFY,
  SIGN_UP,
  UPDATE_ACCOUNT,
  VERIFY_UPDATE_FIELD,
  SIGN_OUT,
} from '../../../constants/endpoints'
import api from '../../../repository/api'
import { ApiError } from '../../../types/api-error.type'
import { SignUpData, UpdateAccountResponse, UserAccount } from '../../../types/user'
import { useAuthStore } from '../stores/auth.store'

type InitiateAuthData = {
  session: string
  preview: string
  otpMedium: 'EMAIL' | 'SMS' // Assuming OTP med ium types
  registrationToken?: string
}

type VerifyAuthData = {
  accessToken: string
  idToken: string
  refreshToken: string
}

export function useAuth() {
  const { setInitSession, resetInitSession, loadSession, resetSession, session } = useAuthStore()

  const [initiateAuthLoading, setInitiateAuthLoading] = useState(false)
  const [initiateAuthError, setInitiateAuthError] = useState<unknown>()
  const [initiateAuthData, setInitiateAuthData] = useState<InitiateAuthData | undefined>()

  const [verifyAuthLoading, setVerifyAuthLoading] = useState(false)
  const [verifAuthError, setVerifyAuthError] = useState<unknown>(undefined)
  const [verifyAuthSuccess, setVerifyAuthSuccess] = useState(false)

  const [refreshTokenLoading, setRefreshTokenLoading] = useState(false)
  const [refreshTokenError, setRefreshTokenError] = useState<unknown>(undefined)
  const [refreshTokenSuccess, setRefreshTokenSuccess] = useState(false)

  const [signUpLoading, setSignUpLoading] = useState(false)
  const [signUpError, setSignUpError] = useState<unknown>(undefined)
  const [signUpSuccess, setSignUpSuccess] = useState(false)

  const [updateAccountLoading, setUpdateAccountLoading] = useState(false)
  const [updateAccountError, setUpdateAccountError] = useState<unknown>(undefined)
  const [updateAccountSuccess, setUpdateAccountSuccess] = useState(false)
  const [updateAccountData, setUpdateAccountData] = useState<UpdateAccountResponse | undefined>()

  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false)
  const [deleteAccountError, setDeleteAccountError] = useState<unknown>(undefined)
  const [deleteAccountSuccess, setDeleteAccountSuccess] = useState(false)

  const [verifyUpdateFieldLoading, setVerifyUpdateFieldLoading] = useState(false)
  const [verifyUpdateFieldError, setVerifyUpdateFieldError] = useState<unknown>(undefined)
  const [verifyUpdateFieldSuccess, setVerifyUpdateFieldSuccess] = useState(false)

  const initiateAuthSession = useCallback(
    async (username: string, otpMedium?: 'EMAIL' | 'SMS', redirectUri?: string, turnstileToken?: string) => {
      setInitiateAuthData(undefined)
      setInitiateAuthError(undefined)
      setInitiateAuthLoading(true)

      try {
        const res = await api.post<ApiResponse<InitiateAuthData>>(SIGN_IN_INITIATE, {
          username,
          otpMedium,
          ...(turnstileToken && { turnstileToken }),
        })
        const data = res.data.data
        setInitiateAuthData(res.data.data)
        setInitSession({
          id: data.session,
          username,
          otpMedium: data.otpMedium,
          preview: data.preview,
          redirectUri,
        })
      } catch (err) {
        if (err instanceof AxiosError) {
          const errData: ApiError = err.response?.data
          setInitiateAuthError(errData)
        } else {
          setInitiateAuthError(err as AxiosError)
        }
      } finally {
        setInitiateAuthLoading(false)
      }
    },
    [setInitSession],
  )

  const verifyAuthSession = useCallback(
    async (username: string, otpMedium: 'EMAIL' | 'SMS', code: string, session: string) => {
      setInitiateAuthData(undefined)
      setVerifyAuthError(undefined)
      setVerifyAuthLoading(true)
      setVerifyAuthSuccess(false)

      try {
        await api.post<ApiResponse<VerifyAuthData>>(SIGN_IN_VERIFY, { username, otpMedium, code, session })
        resetInitSession()
        loadSession()
        setVerifyAuthSuccess(true)
      } catch (err) {
        if (err instanceof AxiosError) {
          const errData: ApiError = err.response?.data
          setVerifyAuthError(errData)
        } else {
          setVerifyAuthError(err as AxiosError)
        }
      } finally {
        setVerifyAuthLoading(false)
      }
    },
    [resetInitSession, loadSession],
  )

  const signOut = useCallback(
    async (global = false) => {
      const deleteCookie = (name: string) => {
        const isLocalhost = window.location.hostname.includes('localhost')

        // Delete cookie with matching attributes (secure and SameSite=None for all environments)
        const cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; SameSite=None`
        document.cookie = cookieString

        // Also try with domain attribute for production
        if (!isLocalhost) {
          document.cookie = `${cookieString}; domain=.pacesports.app`
        }
      }

      try {
        if (global) {
          // Invalidate session server-side
          await api.post(SIGN_OUT, { username: session?.idTokenDecoded['cognito:username'] })
        } else {
          // Delete authentication cookies client-side
          const idCookie = import.meta.env.VITE_ID_COOKIE
          const refreshCookie = import.meta.env.VITE_REFRESH_COOKIE
          const accessCookie = import.meta.env.VITE_ACCESS_COOKIE

          if (idCookie) deleteCookie(idCookie)
          if (refreshCookie) deleteCookie(refreshCookie)
          if (accessCookie) deleteCookie(accessCookie)
        }
      } finally {
        // Always reset session state regardless of API success/failure
        resetInitSession()
        resetSession()
      }
    },
    [resetInitSession, resetSession, session?.idTokenDecoded],
  )

  const refreshToken = useCallback(async () => {
    setRefreshTokenLoading(true)
    setRefreshTokenError(undefined)
    setRefreshTokenSuccess(false)

    try {
      await api.post(REFRESH_TOKEN, {
        refreshToken: session?.refreshToken,
        username: session?.idTokenDecoded['cognito:username'],
      })
      loadSession() // Update the session with new tokens from cookies
      setRefreshTokenSuccess(true)
    } catch (err) {
      setRefreshTokenError(err)
      throw err
    } finally {
      setRefreshTokenLoading(false)
    }
  }, [session, loadSession])

  const signUp = useCallback(async (signUpData: SignUpData) => {
    setSignUpError(undefined)
    setSignUpLoading(true)
    setSignUpSuccess(false)

    try {
      await api.post(SIGN_UP, signUpData)
      setSignUpSuccess(true)
    } catch (err) {
      if (err instanceof AxiosError) {
        const errData: ApiError = err.response?.data
        setSignUpError(errData)
      } else {
        setSignUpError(err as AxiosError)
      }
    } finally {
      setSignUpLoading(false)
    }
  }, [])

  const updateAccount = useCallback(async (data: Partial<UserAccount>) => {
    setUpdateAccountError(undefined)
    setUpdateAccountLoading(true)
    setUpdateAccountSuccess(false)
    setUpdateAccountData(undefined)

    // Changing email or phone triggers a verification code, which requires the
    // access token in the body (the header only carries the ID token). Other
    // profile fields don't need it, so only attach it for contact changes.
    const isContactChange = 'email' in data || 'phoneNumber' in data
    const current = useAuthStore.getState().session
    const body = isContactChange ? { ...data, accessToken: current?.accessToken } : data

    try {
      // 401s are refreshed and retried transparently by the api instance's interceptor.
      const resp = await api.patch<UpdateAccountResponse>(UPDATE_ACCOUNT, body)
      setUpdateAccountData(resp.data)
      setUpdateAccountSuccess(true)
    } catch (err) {
      if (err instanceof AxiosError) {
        const errData: ApiError = err.response?.data
        setUpdateAccountError(errData)
      } else {
        setUpdateAccountError(err as AxiosError)
      }
    } finally {
      setUpdateAccountLoading(false)
    }
  }, [])

  const deleteAccount = useCallback(async () => {
    setDeleteAccountError(undefined)
    setDeleteAccountLoading(true)
    setDeleteAccountSuccess(false)

    try {
      await api.delete(UPDATE_ACCOUNT)

      setDeleteAccountSuccess(true)
      resetInitSession()
      resetSession()
    } catch (err) {
      if (err instanceof AxiosError) {
        const errData: ApiError = err.response?.data
        setDeleteAccountError(errData)
      } else {
        setDeleteAccountError(err as AxiosError)
      }
    } finally {
      setDeleteAccountLoading(false)
    }
  }, [resetInitSession, resetSession])

  const verifyUpdateField = useCallback(
    async (code: string, attributeName: 'email' | 'phone_number') => {
      setVerifyUpdateFieldError(undefined)
      setVerifyUpdateFieldLoading(true)
      setVerifyUpdateFieldSuccess(false)

      try {
        await api.post(VERIFY_UPDATE_FIELD, {
          accessToken: session?.accessToken,
          code,
          attributeName,
        })
        setVerifyUpdateFieldSuccess(true)
      } catch (err) {
        if (err instanceof AxiosError) {
          const errData: ApiError = err.response?.data
          setVerifyUpdateFieldError(errData)
        } else {
          setVerifyUpdateFieldError(err as AxiosError)
        }
      } finally {
        setVerifyUpdateFieldLoading(false)
      }
    },
    [session?.accessToken],
  )

  const memo = useMemo(
    () => ({
      initiateAuthSession,
      initiateAuthLoading,
      initiateAuthError,
      initiateAuthData,

      verifyAuthSession,
      verifyAuthLoading,
      verifAuthError,
      verifyAuthSuccess,

      refreshToken,
      refreshTokenSuccess,
      refreshTokenLoading,
      refreshTokenError,

      signOut,

      signUp,
      signUpLoading,
      signUpError,
      signUpSuccess,

      updateAccount,
      updateAccountData,
      updateAccountLoading,
      updateAccountError,
      updateAccountSuccess,

      deleteAccount,
      deleteAccountLoading,
      deleteAccountError,
      deleteAccountSuccess,

      verifyUpdateField,
      verifyUpdateFieldLoading,
      verifyUpdateFieldError,
      verifyUpdateFieldSuccess,
    }),
    [
      initiateAuthSession,
      initiateAuthLoading,
      initiateAuthError,
      initiateAuthData,
      verifyAuthSession,
      verifyAuthLoading,
      verifAuthError,
      verifyAuthSuccess,
      refreshToken,
      refreshTokenSuccess,
      refreshTokenLoading,
      refreshTokenError,
      signOut,
      signUp,
      signUpLoading,
      signUpError,
      signUpSuccess,
      updateAccount,
      updateAccountData,
      updateAccountLoading,
      updateAccountError,
      updateAccountSuccess,
      deleteAccount,
      deleteAccountLoading,
      deleteAccountError,
      deleteAccountSuccess,
      verifyUpdateField,
      verifyUpdateFieldLoading,
      verifyUpdateFieldError,
      verifyUpdateFieldSuccess,
    ],
  )

  return memo
}
